import { changeWorkspaceLayout } from '../../utils/workspaceLayout';
import { migrateCasePaymentLayout } from './caseWorkspace/casePaymentLayout';
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useParams } from "react-router-dom";
import Board from "@cloudscape-design/board-components/board";
import { Box, SpaceBetween } from "@cloudscape-design/components";
import CaseHeaderWidget from "./caseWorkspace/widgets/CaseHeaderWidget.jsx";
import SupportingDocumentsWidget from "../../widgets/SupportingDocumentsWidget";
import SecureMessageComposePanel from "../../widgets/SecureMessageComposePanel.jsx";
import SecureMessagingWidget from "../../widgets/SecureMessagingWidget";
import CaseNotesWidget from "../../widgets/CaseNotesWidget";
import CaseCalendarWidget from "../../widgets/CaseCalendarWidget";
import ActionPlansWidget from "./caseWorkspace/widgets/ActionPlansWidget.jsx";
import InterventionsWidget from "./caseWorkspace/widgets/InterventionsWidget.jsx";
import FinancePanelWidget from "./caseWorkspace/widgets/FinancePanelWidget.jsx";
import CompliancePanelWidget from "./caseWorkspace/widgets/CompliancePanelWidget.jsx";
import ParticipantDetailsWidget from "./caseWorkspace/widgets/ParticipantDetailsWidget.jsx";
import ExportPreviewWidget from "./caseWorkspace/widgets/ExportPreviewWidget.jsx";
import InterventionAssessmentWidget from "./caseWorkspace/widgets/InterventionAssessmentWidget.jsx";
import CasePaymentRequestsWidget from "./caseWorkspace/widgets/CasePaymentRequestsWidget.jsx";
import CaseWorkspaceCaseHeaderHelp from "../../helpPanelContents/caseWorkspaceCaseHeaderHelp.js";
import SupportingDocumentsHelp from "../../helpPanelContents/supportingDocumentsHelp.js";
import SecureMessagesHelpPanelContent from "../../helpPanelContents/secureMessagesHelpPanelContent.js";
import CaseNotesHelp from "../../helpPanelContents/caseNotesHelp.js";
import CaseCalendarHelp from "../../helpPanelContents/caseCalendarHelp.js";
import CaseWorkspaceActionPlansHelp from "../../helpPanelContents/caseWorkspaceActionPlansHelp.js";
import CaseWorkspaceInterventionsHelp from "../../helpPanelContents/caseWorkspaceInterventionsHelp.js";
import CaseWorkspaceProposedInterventionsHelp from "../../helpPanelContents/caseWorkspaceProposedInterventionsHelp.js";
import CaseWorkspaceFinancePanelHelp from "../../helpPanelContents/caseWorkspaceFinancePanelHelp.js";
import CaseWorkspaceCompliancePanelHelp from "../../helpPanelContents/caseWorkspaceCompliancePanelHelp.js";
import CaseWorkspaceParticipantDetailsHelp from "../../helpPanelContents/caseWorkspaceParticipantDetailsHelp.js";
import CaseWorkspaceExportPreviewHelp from "../../helpPanelContents/caseWorkspaceExportPreviewHelp.js";
import CaseWorkspaceTimelineHelp from "../../helpPanelContents/caseWorkspaceTimelineHelp.js";
import CaseWorkspaceHelp from "../../helpPanelContents/caseWorkspaceHelp.js";
import CasePaymentsHelp from "../../helpPanelContents/casePaymentsHelp.js";
import { CaseWorkspaceProvider, useCaseWorkspace } from "./caseWorkspace/CaseWorkspaceContext.jsx";
import { PaymentsDataProvider } from "../finance/widgets/PaymentsDataContext.jsx";
import PaymentEmailWorkspaceProvider from "../finance/paymentEmail/PaymentEmailWorkspaceProvider.jsx";
import CaseApplicationEventsWidget from "./caseWorkspace/widgets/CaseApplicationEventsWidget.jsx";
import { parseWorkspaceEntry } from "../../utils/approvalWorkspaceEntry.js";

const STORAGE_KEY = "iset-case-workspace-layout-v14";
const TUTORIAL_CASE_LAYOUT_RESET_FLAG = "iset.tutorial.resetCaseWorkspaceLayout";

const widgetRegistry = {
  "supporting-documents": {
    id: "supporting-documents",
    defaultRowSpan: 4,
    defaultColumnSpan: 2,
    component: SupportingDocumentsWidget,
    title: "Supporting documents",
    description: "Unified view of uploaded files and secure message attachments.",
    helpComponent: SupportingDocumentsHelp,
    helpTitle: "Supporting documents",
    aiContext: SupportingDocumentsHelp.aiContext,
  },
  "case-notes": {
    id: "case-notes",
    defaultRowSpan: 4,
    defaultColumnSpan: 2,
    component: CaseNotesWidget,
    title: "Notes and tasks",
    description: "Keep internal notes and follow-ups visible to the case team.",
    helpComponent: CaseNotesHelp,
    helpTitle: "Notes and tasks",
    aiContext: CaseNotesHelp.aiContext,
  },
  "case-calendar": {
    id: "case-calendar",
    defaultRowSpan: 4,
    defaultColumnSpan: 2,
    component: CaseCalendarWidget,
    title: "Case calendar",
    description: "Timeline of reminders, deadlines, and milestones.",
    helpComponent: CaseCalendarHelp,
    helpTitle: "Case calendar",
    aiContext: CaseCalendarHelp.aiContext,
  },
  "secure-messaging": {
    id: "secure-messaging",
    defaultRowSpan: 4,
    defaultColumnSpan: 2,
    component: SecureMessagingWidget,
    title: "Secure messaging",
    description: "Read and send case-linked messages, including attachments.",
    helpComponent: SecureMessagesHelpPanelContent,
    helpTitle: "Secure messaging",
    aiContext: SecureMessagesHelpPanelContent.aiContext,
  },
  caseHeader: {
    id: "caseHeader",
    defaultRowSpan: 2,
    defaultColumnSpan: 4,
    component: CaseHeaderWidget,
    title: "Case header",
    description: null,
    helpComponent: CaseWorkspaceCaseHeaderHelp,
    helpTitle: "Case header",
    aiContext: CaseWorkspaceCaseHeaderHelp.aiContext,
  },
  actionPlans: {
    id: "actionPlans",
    defaultRowSpan: 4,
    defaultColumnSpan: 2,
    component: ActionPlansWidget,
    title: "Action plans",
    description: "Manage action plans and select one to edit interventions.",
    helpComponent: CaseWorkspaceActionPlansHelp,
    helpTitle: "Action plans",
    aiContext: CaseWorkspaceActionPlansHelp.aiContext,
  },
  interventions: {
    id: "interventions",
    defaultRowSpan: 4,
    defaultColumnSpan: 2,
    component: InterventionsWidget,
    title: "Interventions",
    description: "Edit ILMP-compliant intervention records.",
    helpComponent: CaseWorkspaceInterventionsHelp,
    helpTitle: "Interventions",
    aiContext: CaseWorkspaceInterventionsHelp.aiContext,
  },
  interventionAssessment: {
    id: "interventionAssessment",
    defaultRowSpan: 6,
    defaultColumnSpan: 4,
    component: InterventionAssessmentWidget,
    title: "Intervention assessment",
    description: "Draft, update, and review intervention proposals.",
    helpComponent: CaseWorkspaceProposedInterventionsHelp,
    helpTitle: "Proposed interventions",
    aiContext: CaseWorkspaceProposedInterventionsHelp.aiContext,
  },
  financePanel: {
    id: "financePanel",
    defaultRowSpan: 4,
    defaultColumnSpan: 2,
    component: FinancePanelWidget,
    title: "Finance panel",
    description: null,
    helpComponent: CaseWorkspaceFinancePanelHelp,
    helpTitle: "Finance panel",
    aiContext: CaseWorkspaceFinancePanelHelp.aiContext,
  },
  compliancePanel: {
    id: "compliancePanel",
    defaultRowSpan: 4,
    defaultColumnSpan: 2,
    component: CompliancePanelWidget,
    title: "Compliance",
    description: "ILMP and finance validation status.",
    helpComponent: CaseWorkspaceCompliancePanelHelp,
    helpTitle: "Compliance",
    aiContext: CaseWorkspaceCompliancePanelHelp.aiContext,
  },
  exportPreview: {
    id: "exportPreview",
    defaultRowSpan: 4,
    defaultColumnSpan: 2,
    component: ExportPreviewWidget,
    title: "Export preview",
    description: "Review ILMP XML and finance postings before export.",
    helpComponent: CaseWorkspaceExportPreviewHelp,
    helpTitle: "Export preview",
    aiContext: CaseWorkspaceExportPreviewHelp.aiContext,
  },
  "case-events": {
    id: "case-events",
    defaultRowSpan: 5,
    defaultColumnSpan: 4,
    component: CaseApplicationEventsWidget,
    title: "Events timeline",
    description: "Audit trail of case activity, status changes, reminders, and related actions.",
    helpComponent: CaseWorkspaceTimelineHelp,
    helpTitle: "Events timeline",
    aiContext: CaseWorkspaceTimelineHelp.aiContext,
  },
  participantDetails: {
    id: "participantDetails",
    defaultRowSpan: 4,
    defaultColumnSpan: 2,
    component: ParticipantDetailsWidget,
    title: "Participant details",
    description:
      "Refer to the application form for the original submission. Case Managers must keep these details current based on participant updates. Handle this sensitive personal data carefully and avoid duplicating it elsewhere.",
    helpComponent: CaseWorkspaceParticipantDetailsHelp,
    helpTitle: "Participant details",
    aiContext: CaseWorkspaceParticipantDetailsHelp.aiContext,
  },
  "payments-queue": {
    id: "payments-queue",
    defaultRowSpan: 5,
    defaultColumnSpan: 4,
    component: CasePaymentRequestsWidget,
    title: "Payments",
    description: "Prepare payments and review sent requests, documents and follow-up for this case.",
    helpComponent: CasePaymentsHelp,
    helpTitle: "Payments",
    aiContext: CasePaymentsHelp.aiContext,
  },
};

const defaultLayout = [
  { id: "caseHeader", rowSpan: 2, columnSpan: 4 },
  { id: "participantDetails", rowSpan: 8, columnSpan: 2 },
  { id: "actionPlans", rowSpan: 4, columnSpan: 2 },
  { id: "case-calendar", rowSpan: 5, columnSpan: 2 },
  { id: "interventions", rowSpan: 4, columnSpan: 2 },
  { id: "case-notes", rowSpan: 5, columnSpan: 2 },
  { id: "supporting-documents", rowSpan: 3, columnSpan: 2 },
  { id: "secure-messaging", rowSpan: 4, columnSpan: 2 },
  { id: "case-events", rowSpan: 5, columnSpan: 4 },
];

const interventionWorkflowLayout = [
  { id: "caseHeader", rowSpan: 3, columnSpan: 4 },
  { id: "actionPlans", rowSpan: 3, columnSpan: 2 },
  { id: "interventionAssessment", rowSpan: 6, columnSpan: 2 },
  { id: "interventions", rowSpan: 4, columnSpan: 2 },
];

const interventionApprovalLayout = [
  { id: "caseHeader", rowSpan: 3, columnSpan: 4 },
  { id: "interventionAssessment", rowSpan: 6, columnSpan: 4 },
  { id: "participantDetails", rowSpan: 6, columnSpan: 2 },
  { id: "supporting-documents", rowSpan: 6, columnSpan: 2 },
];

const interventionFollowupLayout = [
  { id: "caseHeader", rowSpan: 2, columnSpan: 4 },
  { id: "interventions", rowSpan: 6, columnSpan: 4 },
  { id: "actionPlans", rowSpan: 4, columnSpan: 4 },
  { id: "case-notes", rowSpan: 5, columnSpan: 2 },
  { id: "case-calendar", rowSpan: 5, columnSpan: 2 },
];

const paymentProofLayout = [
  { id: "caseHeader", rowSpan: 2, columnSpan: 4 },
  { id: "payments-queue", rowSpan: 5, columnSpan: 4 },
  { id: "supporting-documents", rowSpan: 4, columnSpan: 4 },
];

const managePlansLayout = [
  { id: "caseHeader", rowSpan: 3, columnSpan: 4 },
  { id: "actionPlans", rowSpan: 3, columnSpan: 4 },
  { id: "interventions", rowSpan: 3, columnSpan: 4 },
];

const notesCalendarLayout = [
  { id: "caseHeader", rowSpan: 3, columnSpan: 4 },
  { id: "case-notes", rowSpan: 6, columnSpan: 2 },
  { id: "case-calendar", rowSpan: 6, columnSpan: 2 },
];

const documentsMessagesLayout = [
  { id: "caseHeader", rowSpan: 3, columnSpan: 4 },
  { id: "supporting-documents", rowSpan: 6, columnSpan: 2 },
  { id: "secure-messaging", rowSpan: 6, columnSpan: 2 },
];

const auditTrailLayout = [
  { id: "caseHeader", rowSpan: 3, columnSpan: 4 },
  { id: "participantDetails", rowSpan: 6, columnSpan: 2 },
  { id: "case-events", rowSpan: 6, columnSpan: 2 },
];

const managePaymentsLayout = [
  { id: "caseHeader", rowSpan: 3, columnSpan: 4 },
  { id: "payments-queue", rowSpan: 5, columnSpan: 4 },
  { id: "interventions", rowSpan: 4, columnSpan: 4 },
  { id: "actionPlans", rowSpan: 4, columnSpan: 4 },
];

const esdcValidationLayout = [
  { id: "caseHeader", rowSpan: 3, columnSpan: 4 },
  { id: "compliancePanel", rowSpan: 6, columnSpan: 2 },
  { id: "exportPreview", rowSpan: 6, columnSpan: 2 },
];

const QUICK_ACTION_LAYOUTS = {
  caseWorkspace: defaultLayout,
  managePlans: managePlansLayout,
  notesCalendar: notesCalendarLayout,
  documentsMessages: documentsMessagesLayout,
  auditTrail: auditTrailLayout,
  managePayments: managePaymentsLayout,
  esdcValidation: esdcValidationLayout,
  proposeIntervention: interventionWorkflowLayout,
  interventionWorkflow: interventionWorkflowLayout,
};

const normalizeLayout = layout => migrateCasePaymentLayout(layout).filter(entry => widgetRegistry[entry.id]);

const exportLayout = items =>
  normalizeLayout(items).map(({ id, rowSpan, columnSpan, columnOffset }) => ({
    id,
    rowSpan,
    columnSpan,
    columnOffset,
  }));

const toBoardItems = layout =>
  normalizeLayout(layout)
    .map(item => {
      const definition = widgetRegistry[item.id];
      if (!definition) return null;
      return {
        id: definition.id,
        rowSpan: item.rowSpan ?? definition.defaultRowSpan,
        columnSpan: item.columnSpan ?? definition.defaultColumnSpan,
        columnOffset: item.columnOffset,
        data: {
          title: definition.title,
          description: definition.description,
          component: definition.component,
          helpComponent: definition.helpComponent,
          helpTitle: definition.helpTitle,
          aiContext: definition.aiContext,
        },
      };
    })
    .filter(Boolean);

const loadLayoutFromStorage = () => {
  if (typeof window === "undefined") return null;
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return null;
    return normalizeLayout(parsed);
  } catch {
    return null;
  }
};

const cloneLayout = layout => normalizeLayout(layout);

const PALETTE_EXCLUDE_IDS = new Set();

const computePaletteItems = items =>
  Object.values(widgetRegistry)
    .filter(def => !PALETTE_EXCLUDE_IDS.has(def.id))
    .filter(def => !items.some(item => item.id === def.id))
    .map(def => ({ id: def.id, data: { title: def.title, description: def.description } }));

const areLayoutsEqual = (a = [], b = []) => {
  if (a === b) return true;
  if (!Array.isArray(a) || !Array.isArray(b) || a.length !== b.length) return false;
  for (let i = 0; i < a.length; i += 1) {
    const left = a[i];
    const right = b[i];
    if (
      !left ||
      !right ||
      left.id !== right.id ||
      (left.rowSpan ?? null) !== (right.rowSpan ?? null) ||
      (left.columnSpan ?? null) !== (right.columnSpan ?? null) ||
      (left.columnOffset ?? null) !== (right.columnOffset ?? null)
    ) {
      return false;
    }
  }
  return true;
};

const boardI18nStrings = {
  liveAnnouncementDndStarted: operation => (operation === "resize" ? "Resizing" : "Dragging"),
  liveAnnouncementDndItemReordered: operation => {
    const position =
      operation.direction === "horizontal"
        ? `column ${operation.placement.x + 1}`
        : `row ${operation.placement.y + 1}`;
    return `Item moved to ${position}.`;
  },
  liveAnnouncementDndItemResized: operation => {
    const base =
      operation.direction === "horizontal"
        ? `columns ${operation.placement.width}`
        : `rows ${operation.placement.height}`;
    const constraint =
      operation.direction === "horizontal"
        ? operation.isMinimalColumnsReached
          ? " (minimal)"
          : ""
        : operation.isMinimalRowsReached
          ? " (minimal)"
          : "";
    return `Item resized to ${base}${constraint}.`;
  },
  liveAnnouncementDndItemInserted: operation => {
    const column = `column ${operation.placement.x + 1}`;
    const row = `row ${operation.placement.y + 1}`;
    return `Item inserted to ${column}, ${row}.`;
  },
  liveAnnouncementDndCommitted: operation => `${operation} committed`,
  liveAnnouncementDndDiscarded: operation => `${operation} discarded`,
  liveAnnouncementItemRemoved: op =>
    op?.item?.data?.title ? `Removed item ${op.item.data.title}.` : "Removed item.",
  navigationAriaLabel: "Case workspace navigation",
  navigationAriaDescription: "Use arrow keys to move between widgets in the case workspace.",
  navigationItemAriaLabel: item => (item ? item.data.title : "Empty"),
};

const CaseWorkspaceSecureMessageComposePanel = () => {
  const workspace = useCaseWorkspace();
  return (
    <SecureMessageComposePanel
      caseId={workspace.caseId}
      caseData={workspace.caseData}
      isCaseWorkspace
      selectedInterventionId={workspace.selectedInterventionId}
      refreshCaseData={workspace.refresh}
    />
  );
};

const CaseWorkspacePage = ({
  updateBreadcrumbs,
  setAvailableItems,
  setSplitPanelOpen,
  toggleHelpPanel,
}) => {
  const { caseId } = useParams();
  const location = useLocation();
  const [proposalRequest, setProposalRequest] = useState(null);
  const workspaceEntry = useMemo(() => parseWorkspaceEntry(location.search), [location.search]);
  const selectedApplicationId = useMemo(() => {
    const params = new URLSearchParams(location.search || "");
    return params.get("applicationId") || params.get("application_id") || null;
  }, [location.search]);
  const isPaymentTask = workspaceEntry?.mode === "task" && workspaceEntry?.purpose === "payment-proof";
  const isTaskEntry = (workspaceEntry?.mode === "approval" && workspaceEntry?.approvalType === "intervention") ||
    (workspaceEntry?.mode === "task" && workspaceEntry?.workspace === "case");
  const entryLayout = isPaymentTask ? paymentProofLayout
    : workspaceEntry?.purpose === "intervention-followup" ? interventionFollowupLayout : interventionApprovalLayout;
  const paymentFilters = useMemo(() => (caseId ? { caseId } : {}), [caseId]);
  const [layout, setLayout] = useState(() =>
    isTaskEntry ? cloneLayout(entryLayout) : loadLayoutFromStorage() ?? cloneLayout(defaultLayout)
  );
  const boardItems = useMemo(() => toBoardItems(layout), [layout]);
  const paletteItems = useMemo(() => computePaletteItems(boardItems), [boardItems]);
  const paletteSignatureRef = useRef(JSON.stringify(paletteItems.map(item => item.id)));
  const entryLayoutAppliedRef = useRef(null);
  const previousTaskEntryRef = useRef(isTaskEntry);

  useEffect(() => {
    if (typeof updateBreadcrumbs === "function") {
      updateBreadcrumbs([
        { text: "Home", href: "/" },
        { text: "ISET Clients", href: "/iset/cases" },
        { text: caseId ?? "Case", href: `/cases/${caseId}` },
      ]);
    }
  }, [updateBreadcrumbs, caseId]);

  useEffect(() => {
    const signature = JSON.stringify(paletteItems.map(item => item.id));
    if (paletteSignatureRef.current !== signature) {
      paletteSignatureRef.current = signature;
      if (typeof setAvailableItems === "function") {
        try {
          setAvailableItems(paletteItems);
        } catch {
          // ignore
        }
      }
    }
    if (isTaskEntry) {
      return;
    }
    if (previousTaskEntryRef.current && !isTaskEntry) {
      return;
    }
    try {
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(exportLayout(boardItems)));
    } catch {
      // ignore storage issues in scaffold
    }
  }, [boardItems, isTaskEntry, paletteItems, setAvailableItems]);

  useEffect(() => {
    const wasApprovalEntry = previousTaskEntryRef.current;
    if (wasApprovalEntry && !isTaskEntry) {
      entryLayoutAppliedRef.current = null;
      const restoredLayout = loadLayoutFromStorage() ?? cloneLayout(defaultLayout);
      setLayout(current => (areLayoutsEqual(current, restoredLayout) ? current : restoredLayout));
    }
    previousTaskEntryRef.current = isTaskEntry;
  }, [isTaskEntry]);

  const handleItemsChange = useCallback(({ detail }) => {
    if (!detail || !Array.isArray(detail.items)) return;
    const next = exportLayout(detail.items);
    changeWorkspaceLayout(layout, next, () => setLayout(current => (areLayoutsEqual(current, next) ? current : next)));
  }, [layout]);

  const renderBoardItem = useCallback(
    (item, actions) => {
      if (!item?.id) return null;
      const definition = widgetRegistry[item.id];
      if (!definition) return null;
      const WidgetComponent = definition.component;
      const enhancedActions = {
        ...actions,
        removeItem: () => {
          if (typeof window !== "undefined") {
            window.dispatchEvent(
              new CustomEvent("iset-case-workspace:widget-removed", { detail: { id: item.id } })
            );
          }
          if (actions && typeof actions.removeItem === "function") {
            actions.removeItem();
          } else {
            setLayout(current => current.filter(entry => entry.id !== item.id));
          }
        },
      };
      return (
        <WidgetComponent
          actions={enhancedActions}
          metadata={{ ...item.data, ...(isPaymentTask ? { entryPacketId: workspaceEntry.packetId, entryLineId: workspaceEntry.lineId, invalidEntryTarget: workspaceEntry.invalidTarget } : {}) }}
          workspaceMode="case"
          workspaceEntry={workspaceEntry}
          proposalRequest={proposalRequest?.caseId === caseId ? proposalRequest : null}
          toggleHelpPanel={toggleHelpPanel}
        />
      );
    },
    [setLayout, toggleHelpPanel, workspaceEntry, proposalRequest, caseId, isPaymentTask]
  );

  const resetLayout = useCallback(() => {
    const baselineLayout = isTaskEntry ? entryLayout : defaultLayout;
    changeWorkspaceLayout(layout, baselineLayout, () => {
      setLayout(current => areLayoutsEqual(current, baselineLayout) ? current : cloneLayout(baselineLayout));
      if (!isTaskEntry) {
        try { window.localStorage.removeItem(STORAGE_KEY); } catch (_) {}
      }
    });
  }, [isTaskEntry, entryLayout, layout]);

  useEffect(() => {
    let shouldReset = false;
    try {
      shouldReset = window.sessionStorage?.getItem(TUTORIAL_CASE_LAYOUT_RESET_FLAG) === "1";
      if (shouldReset) {
        window.sessionStorage?.removeItem(TUTORIAL_CASE_LAYOUT_RESET_FLAG);
      }
    } catch {
      shouldReset = false;
    }
    if (shouldReset) {
      resetLayout();
    }
  }, [caseId, resetLayout]);

  const openPalette = useCallback(() => {
    if (typeof setAvailableItems === "function") {
      try {
        setAvailableItems(paletteItems);
      } catch {
        // ignore
      }
    }
    if (typeof setSplitPanelOpen === "function") {
      setSplitPanelOpen(true);
    }
  }, [paletteItems, setAvailableItems, setSplitPanelOpen]);

  const addWidgetToLayout = useCallback((widgetId, overrides = {}) => {
    if (!widgetId || !widgetRegistry[widgetId]) return;
    setLayout(current => {
      const existing = current.find(item => item.id === widgetId);
      const nextItem = {
        id: widgetId,
        rowSpan: overrides.rowSpan ?? existing?.rowSpan,
        columnSpan: overrides.columnSpan ?? existing?.columnSpan,
        columnOffset: overrides.columnOffset ?? existing?.columnOffset,
      };
      if (existing) {
        const next = current.map(item => (item.id === widgetId ? nextItem : item));
        return areLayoutsEqual(current, next) ? current : next;
      }
      return [...current, nextItem];
    });
  }, []);

  const applyLayout = useCallback((nextLayout, { afterSavedAction = false } = {}) => {
    if (!Array.isArray(nextLayout)) return;
    const normalizedLayout = normalizeLayout(nextLayout);
    const apply = () => setLayout(current => areLayoutsEqual(current, normalizedLayout) ? current : cloneLayout(normalizedLayout));
    // Cancellation has already saved its deliberate discard before supplying a destination.
    if (afterSavedAction) apply();
    else changeWorkspaceLayout(layout, normalizedLayout, apply);
  }, [layout]);

  useEffect(() => {
    const openHandler = () => openPalette();
    const resetHandler = () => resetLayout();
    const interventionWorkflowHandler = event => {
      if (event.type === 'iset:intervention-assessment:new') {
        setProposalRequest({ ...event.detail, caseId, requestId: Date.now() });
      } else {
        setProposalRequest(null);
      }
      applyLayout(interventionWorkflowLayout);
    };
    const setLayoutHandler = event => {
      const detail = event?.detail || {};
      if (detail.layoutId === "caseWorkspace") {
        setProposalRequest(null);
        entryLayoutAppliedRef.current = null;
        // Leaving a task restores the ordinary board; cancellation must not erase its preference.
        applyLayout(loadLayoutFromStorage() ?? defaultLayout, { afterSavedAction: detail.afterSavedAction === true });
        return;
      }
      const nextLayout = Array.isArray(detail.layout) ? detail.layout : QUICK_ACTION_LAYOUTS[detail.layoutId];
      if (nextLayout) {
        applyLayout(nextLayout, { afterSavedAction: detail.afterSavedAction === true });
      }
    };
    const addWidgetHandler = event => {
      const detail = event?.detail || {};
      addWidgetToLayout(detail.id, detail);
    };
    window.addEventListener("iset-case-workspace:openPalette", openHandler);
    window.addEventListener("iset-case-workspace:resetLayout", resetHandler);
    window.addEventListener("iset:intervention-assessment:new", interventionWorkflowHandler);
    window.addEventListener("iset:intervention-assessment:select", interventionWorkflowHandler);
    window.addEventListener("iset-case-workspace:set-layout", setLayoutHandler);
    window.addEventListener("iset-case-workspace:add-widget", addWidgetHandler);
    return () => {
      window.removeEventListener("iset-case-workspace:openPalette", openHandler);
      window.removeEventListener("iset-case-workspace:resetLayout", resetHandler);
      window.removeEventListener("iset:intervention-assessment:new", interventionWorkflowHandler);
      window.removeEventListener("iset:intervention-assessment:select", interventionWorkflowHandler);
      window.removeEventListener("iset-case-workspace:set-layout", setLayoutHandler);
      window.removeEventListener("iset-case-workspace:add-widget", addWidgetHandler);
    };
  }, [addWidgetToLayout, applyLayout, caseId, openPalette, resetLayout]);

  useEffect(() => {
    if (!isTaskEntry) return;
    const approvalKey = `${caseId}:${workspaceEntry.key}`;
    if (entryLayoutAppliedRef.current === approvalKey) return;
    entryLayoutAppliedRef.current = approvalKey;
    applyLayout(entryLayout);
  }, [applyLayout, caseId, isTaskEntry, workspaceEntry, entryLayout]);

  return (
    <CaseWorkspaceProvider caseId={caseId} applicationId={selectedApplicationId}>
      <PaymentsDataProvider key={`${caseId}:${isPaymentTask ? workspaceEntry.key : "ordinary"}`} filters={paymentFilters}
        autoSelectFirst={!isPaymentTask} initialRequestId={isPaymentTask && !workspaceEntry.invalidTarget ? workspaceEntry.packetId : null}>
        <PaymentEmailWorkspaceProvider key={caseId} caseId={caseId}>
        <SpaceBetween size="l">
          <Board
            items={boardItems}
            renderItem={renderBoardItem}
            onItemsChange={handleItemsChange}
            i18nStrings={boardI18nStrings}
            empty={<Box padding="m">No widgets configured.</Box>}
          />
        </SpaceBetween>
        <CaseWorkspaceSecureMessageComposePanel />
        </PaymentEmailWorkspaceProvider>
      </PaymentsDataProvider>
    </CaseWorkspaceProvider>
  );
};

CaseWorkspacePage.pageHelp = CaseWorkspaceHelp;
CaseWorkspacePage.pageHelp.aiContext = CaseWorkspaceHelp.aiContext;

export default CaseWorkspacePage;









